'use strict';
import moment from 'moment';

const d = document,
	w = window;

/**
 * @ngdoc service
 * @name mx.components.Utils
 * @module mx.components
 *
 * @description
 * Utilizes list of heterogeneous methods
 *
 * */
var MxUtils = function () {};

MxUtils.prototype = (function () {
	var result = {
		getCookie: getCookie,
		guid: guid,
		guidEmpty: guidEmpty,
		isGuid: isGuid,
		htmlDecode: htmlDecode,
		htmlEncode: htmlEncode,
		formatJavaScriptBreaks: formatJavaScriptBreaks,
		windowSize: windowSize,
		elementInViewport: elementInViewport,
		arraysEqual: arraysEqual,
		replaceElements: replaceElements,
		removeItemsFromArray: removeItemsFromArray,
		getTypeByName: getTypeByName,
		getSafeScript: getSafeScript,
		convertDateFrom6or8Digits: convertDateFrom6or8Digits,
		isolateLocalStyles: isolateLocalStyles,
		genElemId: genElemId,
		getMomentJsSupportedLocale: getMomentJsSupportedLocale,
		playSound: playSound,
	};

	var decimalSeparator = null;
	Object.defineProperty(result, 'decimalSeparator', {
		get: function () {
			if (decimalSeparator === null) {
				var val = 1.1;

				// Temporary fix for safari number formatting:
				// number.toLocaleString is implemented differently on different browsers.
				// On Safari, it chooses not to display with the person-friendly formatting we're used to.
				// It is supported on safari, but its implementation isn't the same as Chrome or Firefox
				var l = val.toLocaleString();
				if (l === val.toString()) {
					// TODO: change and actually deduce the proper format.
					decimalSeparator = '.';
				} else {
					decimalSeparator = l.substring(1, 2);
				}
			}
			return decimalSeparator;
		},
		set: function () {
			throw new Error('decimalSeparator cannot be set');
		},
	});

	var thousandsDelimiter = null;
	Object.defineProperty(result, 'thousandsDelimiter', {
		get: function () {
			if (thousandsDelimiter === null) {
				var val = 1000;

				// Temporary fix for safari number formatting:
				// number.toLocaleString is implemented differently on different browsers.
				// On Safari, it chooses not to display with the person-friendly formatting we're used to.
				// It is supported on safari, but its implementation isn't the same as in Chrome or Firefox
				var l = val.toLocaleString();
				if (l === val.toString()) {
					// TODO: change and actually deduce the proper format.
					thousandsDelimiter = ',';
				} else {
					thousandsDelimiter = l.substring(1, 2);
				}
			}
			return thousandsDelimiter;
		},
		set: function () {
			throw new Error('thousandsDelimiter cannot be set');
		},
	});

	var uniqueElementIndex = 0;

	return result;

	function playSound(soundPath) {
		const audio = new Audio(soundPath);
		audio.load();
		return audio.play();
	}

	// Converts date like: "311219" or "31122019" to format like: "31.12.2019" or "12/31/2019"
	function convertDateFrom6or8Digits(dateString) {
		var expectedDateFormat = ((moment.localeData() || {})._longDateFormat || {}).L;
		if (['DD.MM.YYYY', 'DD-MM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY'].indexOf(expectedDateFormat) > -1 && /^(\d{6}|\d{8})$/.test(dateString)) {
			var dateParts = dateString.match(/^(\d\d)(\d\d)(\d+)$/);
			dateParts[3] = dateParts[3].length === 2 ? (dateParts[3] > 29 ? '19' : '20') + dateParts[3] : dateParts[3];
			var spacer = expectedDateFormat[2]; // Could be: "-", ".", "/"
			dateString = dateParts[1] + spacer + dateParts[2] + spacer + dateParts[3];
		}
		return dateString;
	}

	function getTypeByName(typeName) {
		var arr = typeName.split('.');

		var type = w;
		for (var i = 0, len = arr.length; i < len; i++) {
			type = type[arr[i]];
		}

		if (typeof type !== 'function') {
			var message = 'Type "' + typeName + '" does not exist.';
			if (console.warn) {
				console.warn(message);
			} else {
				throw new Error(message);
			}
		}

		return type;
	}

	function arraysEqual(a, b, elementsEqualFn) {
		if (a === b) {
			return true;
		}
		if (!Array.isArray(a) || !Array.isArray(b)) {
			return false;
		}
		if (a.length !== b.length) {
			return false;
		}

		const sortedA = a.slice(0).sort();
		const sortedB = b.slice(0).sort();
		for (let i = 0; i < sortedA.length; ++i) {
			const equals = elementsEqualFn ? elementsEqualFn(sortedA[i], sortedB[i]) : sortedA[i] === sortedB[i];
			if (!equals) {
				return false;
			}
		}

		return true;
	}

	function replaceElements(target, source) {
		if (!Array.isArray(target) || !Array.isArray(source)) {
			return target;
		}
		target.splice.apply(target, [0, target.length].concat(source));
		return target;
	}

	function removeItemsFromArray(arr, itemsToRemove) {
		if (!Array.isArray(arr) || !Array.isArray(itemsToRemove)) {
			return;
		}

		const newArr = arr.filter((item) => !itemsToRemove.includes(item));
		replaceElements(arr, newArr);
	}

	function isGuid(value) {
		if (typeof value !== 'string') {
			return false;
		}

		return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);
	}

	function guid() {
		function s4() {
			return Math.floor((1 + Math.random()) * 0x10000)
				.toString(16)
				.substring(1);
		}

		return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
	}

	function guidEmpty() {
		return '00000000-0000-0000-0000-000000000000';
	}

	function htmlDecode(value) {
		if (!value) {
			return '';
		}
		return value
			.replace(/&lt;/g, '<')
			.replace(/&gt;/g, '>')
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'")
			.replace(/&amp;/g, '&');
	}

	function htmlEncode(value) {
		if (!value) {
			return '';
		}
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
			.replace(/{{/, '{<!-- sanitation -->{<!-- sanitation -->');
	}

	function formatJavaScriptBreaks(value) {
		if (!value) {
			return value;
		}
		var parts = value.split('\n');
		if (parts.length > 1) {
			return '<p>' + parts.join('</p><p>') + '</p>';
		} else {
			return value;
		}
	}

	function windowSize() {
		return {
			width: Math.max(d.documentElement.clientWidth, w.innerWidth || 0),
			height: Math.max(d.documentElement.clientHeight, w.innerHeight || 0),
		};
	}

	function getCookie(cookieName) {
		var name = cookieName + '=';
		var ca = document.cookie.split(';');

		for (var i = 0; i < ca.length; i++) {
			var c = ca[i];

			while (c.charAt(0) === ' ') {
				c = c.substring(1);
			}

			if (c.indexOf(name) === 0) {
				return c.substring(name.length, c.length);
			}
		}
		return '';
	}

	function elementInViewport(el) {
		var top = el.offsetTop;
		var left = el.offsetLeft;
		var width = el.offsetWidth;
		var height = el.offsetHeight;

		while (el.offsetParent) {
			el = el.offsetParent;
			top += el.offsetTop;
			left += el.offsetLeft;
		}

		return (
			top >= window.pageYOffset &&
			left >= window.pageXOffset &&
			top + height <= window.pageYOffset + window.innerHeight &&
			left + width <= window.pageXOffset + window.innerWidth
		);
	}

	function getSafeScript(script) {
		// will safety replace to '(($obj || {}).prop1 || {}).prop2'
		var safeScript = script.replace(/\s{2,}/g, '').replace(/(?:[\w$-]+\.)*[\w$-]+/gi, function (p) {
			var arr = p.split('.');
			return arr.length > 1
				? new Array(arr.length).join('(') +
						arr
							.map(function (prop, i) {
								return i < arr.length - 1 ? prop + '||{})' : prop;
							})
							.join('.')
				: p;
		});

		// add spaces before and after separators
		safeScript = safeScript
			.replace(/\|\|/g, ' || ')
			.replace(/&&/g, ' && ')
			.replace(/typeof/g, ' typeof ');

		return safeScript;
	}

	// wrap styles inside <style /> by container selector
	// before: <style> p {color: red;} </style>
	// after:  <style> #mx-isolate-styles-container p {color: red;} </style>
	function isolateLocalStyles(html) {
		var elemID = 'mx-isolate-styles-container-' + Math.random().toFixed(20).substr(2);
		var containerEl = $('<div id="' + elemID + '" />').html(html);
		var doc = document.implementation.createHTMLDocument('');
		// the style will only be parsed once it is added to a document
		doc.body.appendChild(containerEl[0]);
		containerEl.find('style').each(function (_, el) {
			$(el).html(_isolateStyleSheetByContainerSelector(el.sheet, '#' + elemID));
		});
		var resHtml = containerEl[0].outerHTML;
		containerEl.remove();
		return resHtml;
	}

	function _isolateStyleSheetByContainerSelector(styleSheet, containerSelector) {
		let cssText = '';
		Object.keys(styleSheet.cssRules || {}).forEach(function (key) {
			const rule = styleSheet.cssRules[key];
			if (rule.selectorText) {
				const oldSelector = rule.selectorText;
				const newSelector = oldSelector
					.split(',')
					.map((sel) => `${containerSelector} ${sel}`)
					.join(',');
				cssText += rule.cssText.replace(oldSelector, newSelector);
			} else if ((rule.conditionText || (rule.media && rule.media.mediaText)) && rule.cssRules) {
				cssText += '@media ' + (rule.conditionText || rule.media.mediaText) + ' {' + _isolateStyleSheetByContainerSelector(rule, containerSelector) + '}';
			}
		});

		return cssText;
	}

	function genElemId(prefix) {
		return (prefix || 'mx-element-id-') + uniqueElementIndex++;
	}

	function getMomentJsSupportedLocale(language) {
		const availableLocales = [
			/* eslint-disable prettier/prettier */
			'af',
			'ar',
			'ar-dz',
			'ar-kw',
			'ar-ly',
			'ar-ma',
			'ar-sa',
			'ar-tn',
			'az',
			'be',
			'bg',
			'bm',
			'bn',
			'bo',
			'br',
			'bs',
			'ca',
			'cs',
			'cv',
			'cy',
			'da',
			'de',
			'de-at',
			'de-ch',
			'dv',
			'el',
			'en',
			'en-au',
			'en-ca',
			'en-gb',
			'en-ie',
			'en-il',
			'en-in',
			'en-nz',
			'en-sg',
			'eo',
			'es',
			'es-do',
			'es-us',
			'et',
			'eu',
			'fa',
			'fi',
			'fil',
			'fo',
			'fr',
			'fr-ca',
			'fr-ch',
			'fy',
			'ga',
			'gd',
			'gl',
			'gom-deva',
			'gom-latn',
			'gu',
			'he',
			'hi',
			'hr',
			'hu',
			'hy-am',
			'id',
			'is',
			'it',
			'it-ch',
			'ja',
			'jv',
			'ka',
			'kk',
			'km',
			'kn',
			'ko',
			'ku',
			'ky',
			'lb',
			'lo',
			'lt',
			'lv',
			'me',
			'mi',
			'mk',
			'ml',
			'mn',
			'mr',
			'ms',
			'ms-my',
			'mt',
			'my',
			'nb',
			'ne',
			'nl',
			'nl-be',
			'nn',
			'oc-lnc',
			'pa-in',
			'pl',
			'pt',
			'pt-br',
			'ro',
			'ru',
			'sd',
			'se',
			'si',
			'sk',
			'sl',
			'sq',
			'sr',
			'sr-cyrl',
			'ss',
			'sv',
			'sw',
			'ta',
			'te',
			'tet',
			'tg',
			'th',
			'tk',
			'tl-ph',
			'tlh',
			'tr',
			'tzl',
			'tzm',
			'tzm-latn',
			'ug-cn',
			'uk',
			'ur',
			'uz',
			'uz-latn',
			'vi',
			'x-pseudo',
			'yo',
			'zh-cn',
			'zh-hk',
			'zh-mo',
			'zh-tw',
			/* eslint-enable prettier/prettier */
		];
		const lang = language.toLowerCase();
		let index = availableLocales.indexOf(lang);
		if (index === -1 && lang.length > 2) {
			index = availableLocales.indexOf(lang.substr(0, 2));
		}
		return index > -1 ? availableLocales[index] : 'en';
	}
})();

w.mx = w.mx || {};
w.mx.components = w.mx.components || {};
w.mx.components.Utils = new MxUtils();
export const utils = w.mx.components.Utils;
